import * as React from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { useFormikContext } from 'formik'
import { useQueryClient } from 'react-query'
import { LeadFilterSet } from '@user/store/lead/filters'
import { IFilterMode } from '@user/components/Filters/typings/IFilterMode'
import {
  DateRangeFilter,
  Divider,
  IOption,
  MultiSelectFilter,
  NullableFilter,
  TernaryFilter,
  ValueRangeFilter,
} from '@user/components/Filters'
import { ServiceProvider } from './ServiceProvider'
import { SalesFunnelProvider } from './SalesFunnelProvider'
import { SalesFunnelStageProvider } from './SalesFunnelStageProvider'
import { CreatedByProvider } from './CreatedByProvider'
import { PostalCodeProvider } from './PostalCodeProvider'
import { BillToPostalCodeProvider } from './BillToPostalCodeProvider'
import { LocationTypeProvider } from './LocationTypeProvider'
import { DivisionProvider } from './DivisionProvider'
import { AssigneeProvider } from './AssigneeProvider'
import { SalesTeamProvider } from './SalesTeamProvider'
import { LeadSourceProvider } from './LeadSourceProvider'
import { CampaignProvider } from './CampaignProvider'
import { ActivityStatusProvider } from './ActivityStatusProvider'
import { ActivityTypeProvider } from './ActivityTypeProvider'
import { ActivityTypesContextProvider } from '@user/features/Activity/ActivitiyTypes/context'
import { MultiSelectTidalFilter } from '@user/components/Filters/components/MultiSelectFilter'
import { ImportNameProvider, LeadTypeProvider } from '.'
import { useTranslation } from 'react-i18next'
import { SubmittedByProvider } from '@user/components/Leads/LeadFilters/SubmittedByProvider'
import { TargetPestsProvider } from './TargetPestsProvider'
import { AgreementStatusProvider } from './AgreementStatusProvider'
import { KnockStatusFilter } from '@user/components/Filters/components/KnockStatusFilter'
import { useKnockStatusFilterEnabled } from '@user/store/tenantFeatures/hooks/useKnockStatusFilterEnabled'

const filterSalesFunnelStageIdDefaultValue = { mode: 'any', options: [] }

export const LeadFilters: React.FC<IFilterMode> = (props) => {
  const {
    values: {
      filters: {
        salesFunnelId: { value: salesFunnelId },
        salesTeamIds: { value: salesTeamIds },
        salesFunnelStageId,
      },
    },
    setFieldValue,
  } = useFormikContext<LeadFilterSet>()

  const { t } = useTranslation()
  const { reset } = props
  const knockStatusFilterEnabled = useKnockStatusFilterEnabled()

  const client = useQueryClient()

  React.useEffect(() => {
    client.removeQueries(['filters', 'lead'])
    client.removeQueries(['filters', 'shared'])
  }, [client])

  const isOptionEqual = useCallback((optionA: IOption, optionB: IOption) => {
    return optionA.value === optionB.value
  }, [])

  const rOptions = useRef(salesFunnelStageId)
  // update the ref.current when value change
  useEffect(() => {
    rOptions.current = salesFunnelStageId
  }, [salesFunnelStageId])

  React.useEffect(() => {
    if (salesFunnelId?.options?.length == 0) {
      setFieldValue('filters.salesFunnelStageId', {
        ...rOptions.current,
        value: filterSalesFunnelStageIdDefaultValue,
      })
    }
  }, [salesFunnelId, setFieldValue])

  return (
    <>
      <DateRangeFilter
        name="createdDate"
        label="Lead Created Date"
        disableFuture
        reset={reset}
      />
      <DateRangeFilter
        name="disqualifiedDate"
        label="Disqualified Date"
        disableFuture
        reset={reset}
      />
      <DateRangeFilter
        name="nextActionDate"
        label="Next Action Date"
        reset={reset}
      />

      <Divider />

      <MultiSelectTidalFilter
        name="salesTeamIds"
        label="Sales Team"
        component={SalesTeamProvider}
        isOptionEqual={isOptionEqual}
        hideCheckBox={false}
      />

      <MultiSelectTidalFilter
        name="assignee"
        label="Owner"
        component={AssigneeProvider}
        isOptionEqual={isOptionEqual}
        hideCheckBox={false}
        reset={reset}
        dependsOn={salesTeamIds.options}
      />

      <MultiSelectTidalFilter
        name="createdBy"
        label="Lead Created By"
        component={CreatedByProvider}
        isOptionEqual={isOptionEqual}
        hideCheckBox={false}
        reset={reset}
      />

      <MultiSelectFilter
        name="submittedBy"
        label="Submitted By"
        component={SubmittedByProvider}
        reset={reset}
      />

      <Divider />

      <NullableFilter
        name="closed"
        label="Lead Status"
        notNullLabel="Disqualified"
        nullLabel="Open"
        emptyLabel="All"
        reset={reset}
      />

      <Divider />

      <TernaryFilter
        name="hasPestPacLocation"
        label="Has PestPac Location"
        trueLabel="Yes"
        falseLabel="No"
        emptyLabel="All"
        reset={reset}
      />

      <Divider />

      <MultiSelectFilter
        name="agreementStatuses"
        label="Agreement Status"
        component={AgreementStatusProvider}
        reset={reset}
      />

      <Divider />

      <MultiSelectTidalFilter
        name="salesFunnelId"
        label="Sales Funnel"
        component={SalesFunnelProvider}
        isOptionEqual={isOptionEqual}
        hideCheckBox={false}
      />

      <MultiSelectTidalFilter
        name="salesFunnelStageId"
        label="Funnel Stages"
        component={SalesFunnelStageProvider}
        isOptionEqual={isOptionEqual}
        hideCheckBox={false}
        reset={reset}
        dependsOn={salesFunnelId.options}
      />

      <Divider />

      <MultiSelectFilter
        name="services"
        label="Services"
        component={ServiceProvider}
        reset={reset}
      />

      <Divider />

      <MultiSelectFilter
        name="targetPests"
        label="Target Pests"
        component={TargetPestsProvider}
        reset={reset}
      />

      <Divider />

      <ActivityTypesContextProvider>
        <MultiSelectFilter
          name="activityTypeId"
          label="Activity Type"
          component={ActivityTypeProvider}
          reset={reset}
        />
      </ActivityTypesContextProvider>

      <MultiSelectFilter
        name="activityStatus"
        label="Activity Status"
        component={ActivityStatusProvider}
        reset={reset}
      />
      <Divider />

      <ValueRangeFilter
        name="totalLeadValue"
        label="Total Value"
        reset={reset}
      />
      <ValueRangeFilter
        name="initialValue"
        label="Initial Value"
        reset={reset}
      />
      <ValueRangeFilter
        name="recurringValue"
        label="Recurring Value"
        reset={reset}
      />

      <Divider />

      <MultiSelectFilter
        name="locationTypeId"
        label="Location Type"
        component={LocationTypeProvider}
        reset={reset}
      />
      <MultiSelectFilter
        name="divisionId"
        label="Division"
        component={DivisionProvider}
        reset={reset}
      />

      <Divider />

      <MultiSelectFilter
        name="sourceId"
        label="Lead Source"
        component={LeadSourceProvider}
        reset={reset}
      />

      <MultiSelectFilter
        name="campaignId"
        label="Campaign"
        component={CampaignProvider}
        reset={reset}
      />
      <Divider />
      <MultiSelectFilter
        name="type"
        label="Type"
        component={LeadTypeProvider}
        reset={reset}
      />
      <Divider />

      <MultiSelectFilter
        name="postalCode"
        label={t('serviceLocationCode')}
        component={PostalCodeProvider}
        reset={reset}
      />

      <Divider />

      <MultiSelectFilter
        name="billToPostalCode"
        label={t('billToCode')}
        component={BillToPostalCodeProvider}
        reset={reset}
      />

      <Divider />

      <MultiSelectFilter
        name="importId"
        label="Import Name"
        component={ImportNameProvider}
        reset={reset}
      />

      {knockStatusFilterEnabled && (
        <>
          <Divider />

          <MultiSelectTidalFilter
            name="knockStatusSettingId"
            label="Knock Status"
            component={KnockStatusFilter}
            reset={reset}
            hideCheckBox={false}
            isOptionEqual={isOptionEqual}
          />
        </>
      )}
    </>
  )
}
LeadFilters.displayName = 'LeadFilters'
